import { parseServiceSchedule, SHOWN_TAGS } from '@common'
import { INVENTORY_STATUS } from 'common/enums'

/**
 * Format menus with shown tags
 * @param {Array} menus - array of menus
 * @returns {Array} formatted menus with shownTags
 */
export const formatFilteredMenus = menus => {
    return menus.map(product => {
        return {
            ...product,
            shownTags: product.tags.filter(tag => SHOWN_TAGS.includes(tag)),
        }
    })
}

/**
 * Organize menus by category with availability info
 * @param {Array} menus - array of menus
 * @param {Object} options - object containing options
 * @param {Object} options.referenceMoment - moment object to evaluate service availability against
 * @param {boolean} options.isRegularMember - boolean indicating if member is regular member (only Member role)
 * @returns {Object} { categories: Array, filteredMenus: Array }
 */
export const organizeMenus = (menus, options) => {
    const referenceMoment = options?.referenceMoment || null
    const isRegularMember = options?.isRegularMember !== undefined ? options.isRegularMember : true
    const orderedCategories = Array.from(new Set(menus.map(menu => menu.stallName)))

    const processedMenus = menus.map(menu => {
        // Check if all variants are sold out
        const isAllVariantSoldOut = menu.variants.every(variant => variant.inventory?.status === INVENTORY_STATUS.SOLD_OUT)

        // Parse service schedule
        const { isService, servingTimes } = parseServiceSchedule(menu.openTimes, {
            referenceMoment: referenceMoment || undefined,
        })

        // get lowest price from all variants
        const prices = menu.variants.map(v => v.sellPrice).filter(price => price > 0)
        const lowestPrice = Math.min(...prices)

        return {
            ...menu,
            isAllVariantSoldOut,
            // If member is not regular member, ignore serving time restriction
            isService: isRegularMember ? isService : true,
            servingTimes,
            lowestPrice,
        }
    })

    // Split menus into available and unavailable
    const { availableMenus, unavailableMenus } = processedMenus.reduce(
        (acc, menu) => {
            // If member is not regular member, all menus are available
            if (!isRegularMember || menu.isService) {
                acc.availableMenus.push(menu)
            } else {
                acc.unavailableMenus.push(menu)
            }
            return acc
        },
        { availableMenus: [], unavailableMenus: [] }
    )

    const filteredMenus = orderedCategories.map(category => {
        // Find the first menu item that matches this category (stall)
        const firstMenuItem =
            processedMenus.find(m => m.stallName === category) ||
            availableMenus.find(m => m.stallName === category) ||
            unavailableMenus.find(m => m.stallName === category)

        return {
            category,
            stallId: firstMenuItem?.stallId,
            items: [],
        }
    })

    // Add unavailable category at the end if there are any unavailable items
    // But only show unavailable category for regular members
    if (isRegularMember && unavailableMenus.length > 0) {
        filteredMenus.push({
            category: 'Unavailable Items',
            stallId: null,
            items: [],
        })
    }

    // First add all available items to their original categories
    availableMenus.forEach(menu => {
        const categoryIdx = filteredMenus.findIndex(m => m.category === menu.stallName)
        if (categoryIdx !== -1) {
            filteredMenus[categoryIdx].items.push(menu)
        }
    })

    // Then add all unavailable items to the special category (only for regular members)
    if (isRegularMember && unavailableMenus.length > 0) {
        const lastIndex = filteredMenus.length - 1
        filteredMenus[lastIndex].items = unavailableMenus
    }

    return {
        categories: orderedCategories,
        filteredMenus,
    }
}
